/**
 * Быстрый переход в шапке: строка — в адрес уровня.
 *
 * Зачем он в шапке, а не на экране района. Вопрос «а что там у …» задаётся с
 * любого экрана и всегда одинаково: человек знает номер контура или имя
 * хозяйства наизусть и хочет попасть в карточку, а не в список, откуда до
 * карточки ещё два перехода. Пока поле жило на обзоре (IA-16), спросить можно
 * было только вернувшись на обзор — то есть потеряв разобранный контекст.
 *
 * Своего поиска он не заводит. Это адресная книга уровней: тот же
 * `GET /contours?search=`, `GET /actors?search=` и `GET /massifs`, что и у
 * реестров, а результат — адрес из `levelPath`, а не «страница результатов».
 *
 * Два ограничения объявлены честно и видны человеку в подсказке:
 *
 * * **номер контура ищется С НАЧАЛА** (`contour_no__startswith` на сервере),
 *   имя хозяйства и массива — по части. Обещать «поиск по всему» значит
 *   объяснять человеку молчание там, где сервер просто ищет иначе;
 * * **порядок выдачи не переставляется**. Хозяйства идут так, как их отдал
 *   сервер; собственная сортировка «по похожести» была бы оценкой похожести,
 *   которой в корпусе нет, и первый же результат читался бы как «самый
 *   подходящий».
 *
 * Массивы фильтруются на клиенте: у `GET /massifs` параметра поиска нет, а
 * список массивов района — это два десятка строк, которые всё равно приходят
 * целиком. Фильтр сохраняет порядок ответа и ничего не ранжирует.
 */

import { Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAnalysisContext, type AnalysisContextValue } from "../analysis/context";
import { api, type MassifOut } from "../api/client";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { tr } from "../i18n/runtime";
import { DENSITY, levelPath, type LevelId } from "../lib/levels";
import { levelLabel } from "./LevelTrail";
import styles from "./headersearch.module.css";

const m = defineMessages({
  massifNote: { id: "common.kit.search.massifNote", defaultMessage: "массив {name}", description: "Серая пометка под найденным контуром в быстром поиске: в каком массиве он лежит; {name} — имя массива; строчная" },
  farmFallback: { id: "common.kit.search.farmFallback", defaultMessage: "хозяйство #{id}", description: "Имя найденного хозяйства в быстром поиске, когда названия нет; {id} — номер" },
  label: { id: "common.kit.search.label", defaultMessage: "Быстрый переход: номер контура, имя хозяйства или массива", description: "Невидимая подпись поля быстрого поиска в шапке (для экранных дикторов)" },
  placeholder: { id: "common.kit.search.placeholder", defaultMessage: "Контур, хозяйство, массив", description: "Подсказка внутри поля быстрого поиска в шапке; узкое поле, коротко" },
  title: { id: "common.kit.search.title", defaultMessage: "Номер контура ищется с начала строки, имя хозяйства и массива — по части имени", description: "Всплывающая подсказка поля быстрого поиска: как работает совпадение" },
  results: { id: "common.kit.search.results", defaultMessage: "Результаты быстрого перехода", description: "aria-label списка найденного под полем быстрого поиска" },
  loading: { id: "common.kit.search.loading", defaultMessage: "ищем…", description: "Строка в панели быстрого поиска, пока идёт запрос; коротко" },
  optionLabel: { id: "common.kit.search.optionLabel", defaultMessage: "{level} {name}", description: "aria-label найденной строки быстрого поиска; {level} — слово уровня заглавными («КОНТУР»), {name} — номер или имя" },
  optionLabelNote: { id: "common.kit.search.optionLabelNote", defaultMessage: "{level} {name}, {note}", description: "aria-label найденной строки с пометкой; {level} — слово уровня заглавными, {note} — например «массив Кук-Орол»" },
  nothing: { id: "common.kit.search.nothing", defaultMessage: "Ничего не нашли. Номер контура ищется с начала строки, имя хозяйства и массива — по части имени.", description: "Панель быстрого поиска, когда запрос ничего не нашёл; объясняет, как искать" },
  more: {
    id: "common.kit.search.more",
    defaultMessage: "ещё {count, plural, one {# совпадение} few {# совпадения} many {# совпадений} other {# совпадения}} — уточните запрос",
    description: "Строка внизу панели быстрого поиска: выдача обрезана; # — число скрытых совпадений",
  },
  failed: { id: "common.kit.search.failed", defaultMessage: "Поиск не ответил. Повторите запрос.", description: "Панель быстрого поиска, когда сервер не ответил" },
});

/** Одна строка с одного запроса не ищется: префикс «1» — это треть района. */
const MIN_QUERY = 2;
/** Пауза набора: запрос уходит за словом, а не за буквой. */
const DEBOUNCE_MS = 250;
/** Бюджет первого взгляда действует и здесь: список подсказок — не реестр. */
const LIMIT = DENSITY.preview;

interface Hit {
  level: LevelId;
  key: number;
  /** То, что человек знает наизусть: номер, имя. */
  name: string;
  /** Где это. Подпись, а не второй заголовок. */
  note: string | null;
}

type PanelState = "idle" | "loading" | "ready" | "error";

interface MassifCache {
  key: string;
  rows: MassifOut[];
}

/**
 * Список массивов на текущий срез. Кэш привязан к ключу контекста анализа:
 * на другой дате состояния это другой список, и переиспользовать его нельзя.
 */
async function loadMassifs(
  context: AnalysisContextValue,
  cache: { current: MassifCache | null },
): Promise<MassifOut[]> {
  const cached = cache.current;
  if (cached && cached.key === context.key) return cached.rows;
  const list = await api.massifs(context.params);
  cache.current = { key: context.key, rows: list.items };
  return list.items;
}

export function HeaderSearch() {
  const t = useT();
  const context = useAnalysisContext();
  const navigate = useNavigate();
  // Контекст читается в момент запроса и перехода, а не через список
  // зависимостей: пересоздание объекта провайдером не должно перезапускать
  // набранный запрос.
  const contextRef = useRef(context);
  contextRef.current = context;

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<Hit[]>([]);
  const [hidden, setHidden] = useState(0);
  const [state, setState] = useState<PanelState>("idle");
  const [active, setActive] = useState(0);
  const massifCache = useRef<MassifCache | null>(null);
  const listId = useId();

  const text = query.trim();
  const short = text.length < MIN_QUERY;

  useEffect(() => {
    if (short) {
      setHits([]);
      setHidden(0);
      setState("idle");
      return;
    }
    let cancelled = false;
    setState("loading");
    const timer = window.setTimeout(() => {
      const ctx = contextRef.current;
      Promise.all([
        // Хозяйства берутся в том же виде реестра, что и на экране «Хозяйства»
        // (`view_scope` по умолчанию): найденное здесь обязано находиться и там.
        api.contours({ ...ctx.params, search: text, limit: LIMIT }),
        api.actors({ ...ctx.params, search: text, limit: LIMIT }),
        loadMassifs(ctx, massifCache),
      ])
        .then(([contours, actors, massifs]) => {
          if (cancelled) return;
          const needle = text.toLowerCase();
          const matched = massifs.filter((row) =>
            row.canonical_name.toLowerCase().includes(needle),
          );
          const shownMassifs = matched.slice(0, LIMIT);
          setHits([
            ...contours.items.map<Hit>((row) => ({
              level: "contour",
              key: row.id,
              name: row.contour_no,
              note: tr(m.massifNote, { name: row.massif.canonical_name }),
            })),
            ...actors.items.map<Hit>((row) => ({
              level: "actor",
              key: row.id,
              name: row.name ?? tr(m.farmFallback, { id: row.id }),
              note: null,
            })),
            ...shownMassifs.map<Hit>((row) => ({
              level: "massif",
              key: row.id,
              name: row.canonical_name,
              note: null,
            })),
          ]);
          // Скрытое названо числом: иначе короткий список читается как
          // «больше ничего нет», хотя выдача просто обрезана.
          setHidden(
            Math.max(0, contours.page.total - contours.items.length) +
              Math.max(0, actors.page.total - actors.items.length) +
              Math.max(0, matched.length - shownMassifs.length),
          );
          setActive(0);
          setState("ready");
        })
        .catch(() => {
          if (cancelled) return;
          setHits([]);
          setHidden(0);
          setState("error");
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [text, short, context.key]);

  const go = useCallback(
    (hit: Hit) => {
      setOpen(false);
      // Уезжает ТОЛЬКО контекст анализа: строка поиска — локальный фильтр
      // шапки, и на чужом экране она не должна стать другим фильтром.
      navigate({
        pathname: levelPath(hit.level, hit.key),
        search: contextRef.current.linkSearch(),
      });
    },
    [navigate],
  );

  const panelOpen = open && !short;
  const current = panelOpen ? hits[active] : undefined;

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!panelOpen) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, Math.max(0, hits.length - 1)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      // Enter открывает первый результат, пока стрелками не выбран другой.
      const hit = hits[active] ?? hits[0];
      if (hit) go(hit);
    }
  };

  // Панель закрывается, когда фокус ушёл из всего блока. Нажатие по строке
  // результата фокус не уводит (`onMouseDown` ниже), поэтому сюда попадает
  // только настоящий уход — Tab, клик мимо, переход по ссылке.
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  };

  return (
    <div className={styles.root} onBlur={onBlur}>
      <div className={styles.field}>
        <Search className={styles.icon} size={16} strokeWidth={1.75} aria-hidden />
        <label className="visually-hidden" htmlFor="header-search">
          {t(m.label)}
        </label>
        <input
          id="header-search"
          className={styles.input}
          type="search"
          role="combobox"
          autoComplete="off"
          aria-expanded={panelOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={current ? `${listId}-${current.level}-${current.key}` : undefined}
          placeholder={t(m.placeholder)}
          title={t(m.title)}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>

      {panelOpen ? (
        <div
          className={styles.panel}
          id={listId}
          role="listbox"
          aria-label={t(m.results)}
        >
          {state === "loading" ? (
            <div className={styles.note}>{t(m.loading)}</div>
          ) : null}

          {hits.map((hit, index) => (
            <button
              key={`${hit.level}-${hit.key}`}
              id={`${listId}-${hit.level}-${hit.key}`}
              type="button"
              role="option"
              aria-selected={index === active}
              // Подпись уровня и имя — два элемента разметки, но одна фраза
              // для чтения вслух: «КОНТУР 1866, массив …».
              aria-label={
                hit.note
                  ? t(m.optionLabelNote, { level: levelLabel(hit.level).toUpperCase(), name: hit.name, note: hit.note })
                  : t(m.optionLabel, { level: levelLabel(hit.level).toUpperCase(), name: hit.name })
              }
              className={`${styles.option} ${index === active ? styles.optionActive : ""}`}
              onMouseEnter={() => setActive(index)}
              // Фокус остаётся в поле: в Safari и Firefox на macOS кнопка по
              // нажатию фокус не получает, поэтому `blur` поля приходит с
              // пустым `relatedTarget`, панель закрывается между mousedown и
              // mouseup — и `click` до строки уже не доходит. Без этой строки
              // мышь работает только в Chrome.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => go(hit)}
            >
              <span className={styles.level}>
                {levelLabel(hit.level).toUpperCase()}
              </span>
              <span className={styles.name}>{hit.name}</span>
              {hit.note ? <span className={styles.hint}>{hit.note}</span> : null}
            </button>
          ))}

          {state === "ready" && hits.length === 0 ? (
            <div className={styles.note}>
              {t(m.nothing)}
            </div>
          ) : null}

          {state === "ready" && hidden > 0 ? (
            <div className={styles.note}>
              {t(m.more, { count: hidden })}
            </div>
          ) : null}

          {state === "error" ? (
            <div className={styles.note}>{t(m.failed)}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
